import { ApiManager } from './ApiManager';
import {
  AnalyticsEvent,
  AnalyticsConfig,
  PlaymerceCollectData,
  PlaymerceSessionStartData,
  PlaymerceSessionDismissData,
  PlaymerceCollectionViewData,
  PlaymerceCartAddData,
  PlaymerceReminderStartData,
  PlaymerceReminderCartViewData,
  PlaymerceReminderDismissData,
  PlaymerceProductViewData,
  PlaymerceProductViewSizeData,
  PlaymerceCategoryData,
  AnalyticsEndpoint,
} from './types/analytics';
import { EventTrackingRequest } from './types/api';

export class Analytics {
  private config: AnalyticsConfig;

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = {
      timeout: 10000,
      retries: 5,
      ...config,
    };
  }

  private getEndpoint(): AnalyticsEndpoint {
    const apiRoot = process.env.REACT_APP_PLAYMERCE_API_ROOT || 'http://127.0.0.1:8081';
    return {
      url: `${apiRoot}/scroll-and-collect/event`,
      method: 'POST',
    };
  }

  private buildUrl(sessionId?: string): string {
    const endpoint = this.getEndpoint();
    const url = new URL(endpoint.url);

    // Add session_id if provided
    if (sessionId || this.config.sessionId) {
      url.searchParams.append('session_id', sessionId || this.config.sessionId!);
    }

    return url.toString();
  }

  private async sendEvent(event: AnalyticsEvent, sessionId?: string): Promise<void> {
    const url = this.buildUrl(sessionId);

    // Convert the AnalyticsEvent to EventTrackingRequest format
    const eventRequest: EventTrackingRequest = {
      event_name: event.event_name,
      experiments: {},
      data: event.data,
    };

    try {
      await ApiManager.post<void>({ url }, eventRequest, {
        timeout: this.config.timeout,
        retries: this.config.retries,
      });
    } catch (error) {
      console.error('Analytics event failed:', error);
      throw error;
    }
  }

  async playmerce_collect(data: PlaymerceCollectData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_collect',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_product_view(data: PlaymerceProductViewData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_product_view',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_session_start(data: PlaymerceSessionStartData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_session_start',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_session_dismiss(data: PlaymerceSessionDismissData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_session_dismiss',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_collection_view(data: PlaymerceCollectionViewData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_collection_view',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_collection_collapse(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_collection_collapse',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_rules(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_rules',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_size_guide(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'product_view_size_chart',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_variant_product_view(data: PlaymerceProductViewData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_variant_product_view',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_product_view_size(data: PlaymerceProductViewSizeData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_product_view_size',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_product_view_review(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_product_view_review',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_category(data: PlaymerceCategoryData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_category',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_collection_back(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_collection_back',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_product_add(data: PlaymerceProductViewData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_product_add',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_product_back(sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_product_back',
      data: {},
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_cart_add(data: PlaymerceCartAddData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_cart_add',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_reminder_start(data: PlaymerceReminderStartData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_reminder_start',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_reminder_cart_view(data: PlaymerceReminderCartViewData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_reminder_cart_view',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  async playmerce_reminder_dismiss(data: PlaymerceReminderDismissData, sessionId?: string): Promise<void> {
    const event: AnalyticsEvent = {
      event_name: 'playmerce_reminder_dismiss',
      data,
      experiments: {},
    };

    await this.sendEvent(event, sessionId);
  }

  updateConfig(newConfig: Partial<AnalyticsConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  getConfig(): AnalyticsConfig {
    return { ...this.config };
  }
}
